"use client";

import { useEffect, useMemo, useState } from "react";
import { FilePlus2, Loader2, X } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import type { Project, Team } from "@/lib/types";
import {
  normalizeWorkspaceTemplate,
  suggestedWorkspaceTemplateTitle,
  type WorkspaceTemplateRecord,
} from "@/lib/workspaceTemplates";
import { cn } from "@/lib/utils";

interface TemplatePickerProps {
  workspaceId: string;
  projects: Project[];
  teams: Team[];
  onCreate: (input: {
    templateId: string;
    title: string;
    projectId: string | null;
    teamId: string | null;
  }) => void | Promise<void>;
  onClose: () => void;
}

export function TemplatePicker({
  workspaceId,
  projects,
  teams,
  onCreate,
  onClose,
}: TemplatePickerProps) {
  const [templates, setTemplates] = useState<WorkspaceTemplateRecord[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [projectId, setProjectId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetch(
          `/api/workspaces/${encodeURIComponent(workspaceId)}/templates`,
          { signal: controller.signal, cache: "no-store" }
        );
        const payload = (await response.json()) as {
          templates?: WorkspaceTemplateRecord[];
          error?: string;
        };
        if (!response.ok) throw new Error(payload.error || "Could not load templates");
        const rows = payload.templates ?? [];
        setTemplates(rows);
        if (rows[0]) {
          setSelectedId(rows[0].id);
          setTitle(
            suggestedWorkspaceTemplateTitle(normalizeWorkspaceTemplate(rows[0]))
          );
        }
      } catch (caught) {
        if ((caught as Error).name !== "AbortError") {
          setError((caught as Error).message);
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [workspaceId]);

  const selected = templates.find((template) => template.id === selectedId);
  const normalized = useMemo(
    () => (selected ? normalizeWorkspaceTemplate(selected) : null),
    [selected]
  );

  function selectTemplate(template: WorkspaceTemplateRecord) {
    setSelectedId(template.id);
    setTitle(suggestedWorkspaceTemplateTitle(normalizeWorkspaceTemplate(template)));
    setError("");
  }

  function requestClose() {
    if (!creating) onClose();
  }

  async function create() {
    if (!selected || !normalized || !title.trim()) return;
    setCreating(true);
    setError("");
    try {
      await onCreate({
        templateId: selected.id,
        title: title.trim(),
        projectId: projectId || null,
        teamId: teamId || null,
      });
      onClose();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setCreating(false);
    }
  }

  return (
    <Modal
      open
      onClose={requestClose}
      width={820}
      ariaLabelledBy="template-picker-title"
    >
      <div
        className="flex min-h-0 flex-1 flex-col"
        aria-busy={loading || creating}
      >
        <div className="flex items-center gap-2 border-b border-[var(--border)] px-4 py-3">
          <FilePlus2 size={14} className="text-[var(--accent)]" />
          <div className="min-w-0 flex-1">
            <h2
              id="template-picker-title"
              className="text-[13.5px] font-medium tracking-tight"
            >
              Create from a template
            </h2>
            <p className="text-[11.5px] text-[var(--fg-tertiary)]">
              Start with useful structure, then make it yours.
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={creating}
            className="flex h-7 w-7 items-center justify-center rounded-md text-[var(--fg-tertiary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Close template picker"
          >
            <X size={14} />
          </button>
        </div>

        {loading ? (
          <div className="flex min-h-[320px] items-center justify-center text-[12px] text-[var(--fg-tertiary)]">
            <Loader2 size={14} className="mr-2 animate-spin" />
            Loading templates…
          </div>
        ) : error && templates.length === 0 ? (
          <div
            role="alert"
            className="min-h-[320px] px-6 py-12 text-center text-[12.5px] text-[var(--red)]"
          >
            {error}
          </div>
        ) : templates.length === 0 ? (
          <div className="flex min-h-[320px] items-center justify-center px-6 py-12 text-center">
            <div className="max-w-[360px]">
              <h3 className="text-[13.5px] font-medium text-[var(--fg)]">
                No templates available
              </h3>
              <p className="mt-1 text-[11.5px] leading-5 text-[var(--fg-tertiary)]">
                Add a workspace template before creating a doc from one.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid min-h-0 flex-1 md:grid-cols-[300px_1fr]">
            <div className="scrollbar-thin overflow-y-auto border-b border-[var(--border)] p-3 md:border-b-0 md:border-r">
              <div className="mb-2 px-1 text-[10.5px] uppercase tracking-wider text-[var(--fg-muted)]">
                Templates
              </div>
              <div className="space-y-1">
                {templates.map((template) => {
                  const shape = normalizeWorkspaceTemplate(template);
                  const active = selectedId === template.id;
                  return (
                    <button
                      key={template.id}
                      type="button"
                      aria-pressed={active}
                      disabled={creating}
                      onClick={() => selectTemplate(template)}
                      className={cn(
                        "w-full rounded-md border px-3 py-2 text-left transition-colors",
                        active
                          ? "border-[var(--accent)] bg-[var(--accent-bg)]"
                          : "border-transparent hover:border-[var(--border)] hover:bg-[var(--bg-hover)]"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[16px]">{shape.icon}</span>
                        <span className="text-[12.5px] font-medium">
                          {template.name}
                        </span>
                      </div>
                      <p className="mt-1 line-clamp-2 text-[11px] leading-4 text-[var(--fg-tertiary)]">
                        {template.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {selected && normalized && (
              <div className="scrollbar-thin overflow-y-auto p-4">
                <label className="block text-[11px] font-medium text-[var(--fg-secondary)]">
                  Title
                  <input
                    autoFocus
                    value={title}
                    placeholder={normalized.parentTitlePattern ?? undefined}
                    maxLength={240}
                    disabled={creating}
                    onChange={(event) => setTitle(event.target.value)}
                    className="mt-1.5 w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-3 py-1.5 text-[13px] outline-none transition-colors focus:border-[var(--accent)]"
                  />
                </label>
                {normalized.parentTitlePattern && (
                  <p className="mt-1 text-[10.5px] text-[var(--fg-muted)]">
                    Naming pattern: {normalized.parentTitlePattern}
                  </p>
                )}
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <label className="text-[11px] font-medium text-[var(--fg-secondary)]">
                    Project
                    <select
                      value={projectId}
                      disabled={creating}
                      onChange={(event) => setProjectId(event.target.value)}
                      className="mt-1.5 w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1.5 text-[12px] outline-none transition-colors focus:border-[var(--accent)]"
                    >
                      <option value="">No project</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-[11px] font-medium text-[var(--fg-secondary)]">
                    Team
                    <select
                      value={teamId}
                      disabled={creating}
                      onChange={(event) => setTeamId(event.target.value)}
                      className="mt-1.5 w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1.5 text-[12px] outline-none transition-colors focus:border-[var(--accent)]"
                    >
                      <option value="">No team</option>
                      {teams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="mt-4 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-3">
                  <div className="mb-2 text-[10.5px] uppercase tracking-wider text-[var(--fg-muted)]">
                    Included sections
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {normalized.sections.map((section) => (
                      <span
                        key={section}
                        className="rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1 text-[11.5px] text-[var(--fg-secondary)]"
                      >
                        {section}
                      </span>
                    ))}
                  </div>
                </div>
                {normalized.children.length > 0 && (
                  <div className="mt-3 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-3">
                    <div className="mb-2 text-[10.5px] uppercase tracking-wider text-[var(--fg-muted)]">
                      Child docs created with the parent
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {normalized.children.map((child) => (
                        <span
                          key={`${child.icon}-${child.title}`}
                          className="rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1 text-[11.5px] text-[var(--fg-secondary)]"
                        >
                          {child.icon} {child.title}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {error && (
                  <p
                    role="alert"
                    className="mt-3 text-[11.5px] text-[var(--red)]"
                  >
                    {error}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-[var(--border)] px-4 py-3">
          <button
            type="button"
            onClick={requestClose}
            disabled={creating}
            className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-1.5 text-[12px] font-medium text-[var(--fg-secondary)] transition-colors hover:bg-[var(--bg-hover)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void create()}
            disabled={!selected || !title.trim() || creating}
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[var(--accent-hover)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {creating ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <FilePlus2 size={13} />
            )}
            {creating
              ? "Creating…"
              : normalized?.children.length
                ? "Create document set"
                : "Create doc"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
